if (!window.GUMODEMainProductCustomLoaded) {
  window.GUMODEMainProductCustomLoaded = true;

  (() => {
    const rootSelector = 'product-info[data-main-product-custom]';
    const fabControllers = new Map();
    const stickyAtcControllers = new Map();
    const leadTimeControllers = new Map();
    const fabRetryTimers = new Map();

    const getSectionKey = (root) => root.dataset.section || root.id;

    const queryRoots = (scope = document) => {
      if (!scope) return [];

      return scope.matches?.(rootSelector)
        ? [scope]
        : Array.from(scope.querySelectorAll(rootSelector));
    };

    const clearFabRetry = (sectionKey) => {
      const timeoutId = fabRetryTimers.get(sectionKey);
      if (!timeoutId) return;

      window.clearTimeout(timeoutId);
      fabRetryTimers.delete(sectionKey);
    };

    const destroyLeadTime = (sectionKey) => {
      const cleanup = leadTimeControllers.get(sectionKey);
      if (!cleanup) return;

      cleanup();
    };

    const initLeadTime = (root) => {
      const sectionKey = getSectionKey(root);
      destroyLeadTime(sectionKey);

      const blockEl = root.querySelector('.js-leadtime-block');
      const leadEl = root.querySelector('.js-variant-leadtime');
      const dataEl = root.querySelector('.js-variant-leadtime-data');

      if (!blockEl || !leadEl || !dataEl) return;

      let variantLeadTimes = {};
      try {
        variantLeadTimes = JSON.parse(dataEl.textContent || '{}');
      } catch (error) {
        console.error(error);
      }

      const renderLeadTime = (variantId) => {
        const text = (variantLeadTimes[String(variantId)] || '').trim();
        blockEl.style.display = text ? '' : 'none';
        leadEl.textContent = text;
      };

      const handleVariantInputChange = (event) => {
        const target = event.target;
        if (target?.matches('form[action*="/cart/add"] [name="id"]')) {
          renderLeadTime(target.value);
        }
      };

      const handleVariantChange = (event) => {
        const variant = event.detail?.variant;
        if (variant?.id) {
          renderLeadTime(variant.id);
        }
      };

      const idInput = root.querySelector('form[action*="/cart/add"] [name="id"]');
      if (idInput?.value) {
        renderLeadTime(idInput.value);
      } else {
        blockEl.style.display = 'none';
      }

      root.addEventListener('change', handleVariantInputChange);
      document.addEventListener('variant:change', handleVariantChange);

      leadTimeControllers.set(sectionKey, () => {
        root.removeEventListener('change', handleVariantInputChange);
        document.removeEventListener('variant:change', handleVariantChange);
        leadTimeControllers.delete(sectionKey);
      });
    };

    const destroyFab = (sectionKey) => {
      clearFabRetry(sectionKey);

      const controller = fabControllers.get(sectionKey);
      if (!controller) return;

      controller.button.removeEventListener('click', controller.onClick);
      window.removeEventListener('scroll', controller.onUpdate);
      window.removeEventListener('resize', controller.onUpdate);
      document.removeEventListener('variant:change', controller.onDeferredUpdate);
      document.removeEventListener('change', controller.onDeferredUpdate);
      fabControllers.delete(sectionKey);
    };

    const destroyStickyAtc = (sectionKey) => {
      const controller = stickyAtcControllers.get(sectionKey);
      if (!controller) return;

      controller.actionButtons.forEach((button) => button.removeEventListener('click', controller.onAction));
      controller.launchButtons.forEach((button) => button.removeEventListener('click', controller.onLaunch));
      controller.closeButtons.forEach((button) => button.removeEventListener('click', controller.onClose));
      controller.drawer.removeEventListener('keyup', controller.onKeyup);
      controller.drawerSubmit?.removeEventListener('click', controller.onClose);
      controller.quantityMinus?.removeEventListener('click', controller.onQuantityMinus);
      controller.quantityPlus?.removeEventListener('click', controller.onQuantityPlus);
      controller.quantityInput?.removeEventListener('change', controller.onQuantityChange);
      controller.reviewLinks.forEach((button) => button.removeEventListener('click', controller.onReviewLink));
      controller.root.removeEventListener('click', controller.onAnyGiftClick, true);
      controller.root.removeAttribute('data-sticky-atc-ready');
      controller.cancelCloseCleanup?.();
      controller.clearVariantPending?.();
      controller.cleanupAnyGiftSkeleton?.();
      controller.releaseDrawerFocus?.(false);
      controller.drawer.classList.remove('is-open');
      controller.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('gm-sticky-atc-lock');
      controller.mutationObserver?.disconnect();
      controller.mainButtonObserver?.disconnect();
      controller.variantUnsubscriber?.();
      controller.changeUnsubscriber?.();
      window.removeEventListener('resize', controller.onUpdate);
      if (controller.originalParent && controller.originalNextSibling) {
        controller.originalParent.insertBefore(controller.bar, controller.originalNextSibling);
      } else {
        controller.originalParent?.appendChild(controller.bar);
      }
      if (controller.drawerOriginalParent && controller.drawerOriginalNextSibling) {
        controller.drawerOriginalParent.insertBefore(controller.drawer, controller.drawerOriginalNextSibling);
      } else {
        controller.drawerOriginalParent?.appendChild(controller.drawer);
      }
      stickyAtcControllers.delete(sectionKey);
    };

    const initStickyAtc = (root) => {
      const sectionKey = getSectionKey(root);
      destroyStickyAtc(sectionKey);
      root.removeAttribute('data-sticky-atc-ready');

      const bar = root.querySelector('[data-sticky-atc]');
      const stickyPlaceholder = document.querySelector('[data-sticky-atc-placeholder]');
      const actionButtons = Array.from(root.querySelectorAll('[data-sticky-atc-open]'));
      const launchButtons = Array.from(root.querySelectorAll('[data-sticky-atc-launch]'));
      const drawer = root.querySelector('[data-sticky-atc-drawer]');
      const drawerPanel = drawer?.querySelector('.gm-sticky-atc-drawer__panel');
      const closeButtons = Array.from(root.querySelectorAll('[data-sticky-atc-close]'));
      const drawerSubmit = drawer?.querySelector('[data-sticky-atc-submit]');
      const buttonText = bar?.querySelector('[data-sticky-atc-button-text]');
      const drawerButtonText = drawer?.querySelector('[data-sticky-atc-drawer-button-text]');
      const price = bar?.querySelector('[data-sticky-atc-price]');
      const drawerPrice = drawer?.querySelector('[data-sticky-atc-drawer-price]');
      const preview = drawer?.querySelector('[data-sticky-atc-preview]');
      let previewImage = drawer?.querySelector('[data-sticky-atc-preview-image]');
      const previewOptions = drawer?.querySelector('[data-sticky-atc-preview-options]');
      const optionsContainer = drawer?.querySelector('[data-sticky-atc-options]');
      const variantsData = drawer?.querySelector('[data-sticky-atc-variants]');
      const leadTimeRow = drawer?.querySelector('[data-sticky-atc-leadtime-row]');
      const leadTimeText = drawer?.querySelector('[data-sticky-atc-leadtime]');
      const quantityRow = drawer?.querySelector('[data-sticky-atc-quantity-row]');
      const quantityInput = drawer?.querySelector('[data-sticky-atc-quantity-input]');
      const quantityMinus = drawer?.querySelector('[data-sticky-atc-quantity-minus]');
      const quantityPlus = drawer?.querySelector('[data-sticky-atc-quantity-plus]');
      const reviewLinks = Array.from(root.querySelectorAll('[data-gm-review-link]'));
      const mainButton = root.querySelector('.product-form__submit');
      const isStudioPurchase = root.dataset.purchaseStyle === 'studio';
      const anyGiftButtonSelector = '.anygift-e-gift-modal__open-button';
      const anyGiftSkeleton = root.querySelector('[data-anygift-skeleton]');

      if (!bar || !drawer || !drawerPanel || !drawerSubmit || !buttonText || !drawerButtonText || !price || !drawerPrice || !optionsContainer || !leadTimeRow || !leadTimeText || !quantityRow || !quantityInput || !quantityMinus || !quantityPlus || !mainButton) return;

      const originalParent = bar.parentElement;
      const originalNextSibling = bar.nextSibling;
      const drawerOriginalParent = drawer.parentElement;
      const drawerOriginalNextSibling = drawer.nextSibling;

      if (stickyPlaceholder && bar.parentElement !== stickyPlaceholder) {
        stickyPlaceholder.appendChild(bar);
      }

      const mountDrawer = () => {
        const layerRoot = document.querySelector('[data-gm-layer-root]');
        if (layerRoot && drawer.parentElement !== layerRoot) {
          layerRoot.appendChild(drawer);
        }
      };

      mountDrawer();

      const getMainButtonText = () => mainButton.querySelector('span')?.textContent?.trim() || buttonText.textContent.trim();
      const getStickyButtonText = () => {
        const text = getMainButtonText();
        if (text === window.variantStrings?.addToCart) return 'カートに入れる';
        return text;
      };

      const getVariantPicker = () => root.querySelector('variant-selects');
      const getHeaderOffset = () => {
        const header =
          document.querySelector('#shopify-section-header') ||
          document.querySelector('.section-header') ||
          document.querySelector('header.header');

        return header ? header.getBoundingClientRect().height + 12 : 12;
      };
      const getMainQuantityInput = () => root.querySelector('.quantity__input[name="quantity"]');
      const hasSelectableOptions = () => Boolean(getVariantPicker()?.querySelector('input:not(:disabled), select option:not(:disabled)'));
      const canOpenDrawer = () => hasSelectableOptions() || !mainButton.hasAttribute('disabled');
      let productVariants = [];
      try {
        productVariants = JSON.parse(variantsData?.textContent || '[]');
      } catch (error) {
        console.error(error);
      }
      let closeTimeout = null;
      let closeTransitionHandler = null;
      let focusFrame = null;
      let previousFocus = null;
      let afterDrawerClose = null;
      let drawerIntent = 'cart';
      let allowNativeAnyGiftClick = false;
      let variantPending = false;
      let variantPendingTimeout = null;
      let anyGiftSkeletonTimeout = null;
      let anyGiftSkeletonObserver = null;
      let currentUnitPriceText = bar.dataset.stickyAtcUnitPrice || price.textContent.trim();

      const getAnyGiftButton = () => root.querySelector(anyGiftButtonSelector);
      const cleanupAnyGiftSkeleton = () => {
        if (anyGiftSkeletonTimeout) {
          window.clearTimeout(anyGiftSkeletonTimeout);
          anyGiftSkeletonTimeout = null;
        }
        anyGiftSkeletonObserver?.disconnect();
        anyGiftSkeletonObserver = null;
      };
      const resolveAnyGiftSkeleton = () => {
        if (!anyGiftSkeleton || !getAnyGiftButton()) return false;

        anyGiftSkeleton.hidden = true;
        cleanupAnyGiftSkeleton();
        return true;
      };
      const setDrawerIntent = (intent = 'cart') => {
        drawerIntent = intent === 'gift' ? 'gift' : 'cart';
        drawer.dataset.purchaseIntent = drawerIntent;
      };

      const syncButton = () => {
        const disabled = mainButton.hasAttribute('disabled') || mainButton.getAttribute('aria-disabled') === 'true';
        const drawerAvailable = canOpenDrawer();
        const giftIntentActive = isDrawerOpen() && drawerIntent === 'gift';
        const giftUnavailable = variantPending || !getAnyGiftButton() || !getSelectedVariant()?.available;

        launchButtons.forEach((button) => {
          button.toggleAttribute('disabled', !drawerAvailable);
          button.setAttribute('aria-disabled', drawerAvailable ? 'false' : 'true');
        });
        actionButtons.forEach((button) => {
          const shouldDisable = giftIntentActive
            ? giftUnavailable
            : isStudioPurchase
              ? disabled
              : isDrawerOpen()
                ? disabled
                : !drawerAvailable;
          button.toggleAttribute('disabled', shouldDisable);
          button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
        });
        drawerSubmit.toggleAttribute('disabled', disabled);
        drawerSubmit.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        buttonText.textContent = giftIntentActive
          ? getAnyGiftButton()?.textContent?.trim() || getStickyButtonText()
          : isDrawerOpen()
            ? getStickyButtonText()
            : 'カートに入れる';
        drawerButtonText.textContent = getStickyButtonText();
      };

      const getCurrentPriceText = () => {
        const priceContainer = root.querySelector(`#price-${sectionKey}`);
        const priceElement =
          priceContainer?.querySelector('.price__sale .price-item--sale') ||
          priceContainer?.querySelector('.price__regular .price-item--regular:not(.price-item--msrp):not(.variant-item__old-price)') ||
          priceContainer?.querySelector('.price__regular .price-item--regular') ||
          priceContainer?.querySelector('.price-item');

        return priceElement?.textContent?.trim();
      };

      const parseMoneyText = (text) => {
        const match = (text || '').trim().match(/^([^0-9+-]*)([+-]?[0-9][0-9,.\s]*)(.*)$/);
        if (!match) return null;

        const [, prefix, rawNumber, suffix] = match;
        const cleanNumber = rawNumber.replace(/\s/g, '');
        const lastComma = cleanNumber.lastIndexOf(',');
        const lastDot = cleanNumber.lastIndexOf('.');
        const decimalSeparator =
          lastComma > -1 && lastDot > -1
            ? lastComma > lastDot
              ? ','
              : '.'
            : lastDot > -1 && cleanNumber.length - lastDot - 1 !== 3
              ? '.'
              : lastComma > -1 && cleanNumber.length - lastComma - 1 !== 3
                ? ','
                : '';
        const decimalLength = decimalSeparator ? cleanNumber.length - cleanNumber.lastIndexOf(decimalSeparator) - 1 : 0;
        const normalized =
          decimalSeparator === ','
            ? cleanNumber.replace(/\./g, '').replace(',', '.')
            : decimalSeparator === '.'
              ? cleanNumber.replace(/,/g, '')
              : cleanNumber.replace(/[,.]/g, '');
        const amount = Number(normalized);

        if (!Number.isFinite(amount)) return null;

        return {
          amount,
          decimalLength,
          prefix,
          suffix
        };
      };

      const getCurrentQuantity = () => {
        const input = getMainQuantityInput();
        const min = parseInt(quantityInput.min || input?.min || input?.dataset.min || '1');
        const value = parseInt(quantityInput.value || input?.value || String(min));

        return Number.isFinite(value) ? Math.max(value, min) : min;
      };

      const formatSubtotalPrice = (unitPriceText, quantity) => {
        const parsed = parseMoneyText(unitPriceText);
        if (!parsed) return unitPriceText;

        const subtotal = parsed.amount * quantity;
        const formattedNumber = subtotal.toLocaleString(document.documentElement.lang || undefined, {
          minimumFractionDigits: parsed.decimalLength,
          maximumFractionDigits: parsed.decimalLength
        });

        return `${parsed.prefix}${formattedNumber}${parsed.suffix}`;
      };

      const syncSubtotalPrice = () => {
        const nextPrice = formatSubtotalPrice(currentUnitPriceText, getCurrentQuantity());

        if (nextPrice) {
          price.textContent = nextPrice;
          drawerPrice.textContent = nextPrice;
        }
      };

      const syncPrice = (html) => {
        const source = html?.getElementById?.(`price-${sectionKey}`) || root.querySelector(`#price-${sectionKey}`);
        const priceElement =
          source?.querySelector('.price__sale .price-item--sale') ||
          source?.querySelector('.price__regular .price-item--regular:not(.price-item--msrp):not(.variant-item__old-price)') ||
          source?.querySelector('.price__regular .price-item--regular') ||
          source?.querySelector('.price-item');
        const nextPrice = priceElement?.textContent?.trim() || getCurrentPriceText();

        if (nextPrice) {
          currentUnitPriceText = nextPrice;
          bar.dataset.stickyAtcUnitPrice = nextPrice;
          syncSubtotalPrice();
        }
      };

      const syncLeadTime = () => {
        const text = root.querySelector('.js-variant-leadtime')?.textContent?.trim();
        leadTimeRow.hidden = !text;
        leadTimeText.textContent = text || '';
      };

      const getSelectedOptionsText = () => {
        const picker = getVariantPicker();
        const fallbackTitle =
          root.querySelector('.studio-purchase-header__title, .product__title h1')?.textContent?.trim() || '';
        if (!picker) return fallbackTitle;

        return (
          Array.from(picker.querySelectorAll('.product-form__input'))
            .map((group, groupIndex) => {
              const optionName = getOptionName(group, `Option ${groupIndex + 1}`);
              const selectedInput = group.querySelector('input[type="radio"]:checked');
              const selectedSelect = group.querySelector('select');
              const selectedValue = selectedInput?.value || selectedSelect?.selectedOptions?.[0]?.textContent?.trim();

              return selectedValue ? `${optionName}: ${selectedValue}` : '';
            })
            .filter(Boolean)
            .join('\n') || fallbackTitle
        );
      };

      const ensurePreviewImage = () => {
        if (previewImage || !preview) return previewImage;

        const image = document.createElement('img');
        image.width = 80;
        image.loading = 'lazy';
        image.setAttribute('data-sticky-atc-preview-image', '');
        preview.querySelector('.gm-sticky-atc-drawer__preview-media')?.appendChild(image);
        previewImage = image;
        return image;
      };

      const getPreviewImageFromHtml = (html, variant) => {
        const mediaId = variant?.featured_media?.id;
        const source =
          (mediaId && html?.querySelector?.(`[data-media-id="${sectionKey}-${mediaId}"] img`)) ||
          html?.querySelector?.('media-gallery [data-media-id] img') ||
          root.querySelector('media-gallery .is-active img') ||
          root.querySelector('media-gallery [data-media-id] img');

        return source
          ? {
              src: source.currentSrc || source.src,
              alt: source.alt || ''
            }
          : null;
      };

      const syncPreview = (variant, html) => {
        if (!preview || !previewOptions) return;

        const imageData =
          variant?.featured_media?.preview_image?.src
            ? {
                src: variant.featured_media.preview_image.src,
                alt: variant.featured_media.alt || variant.title || ''
              }
            : getPreviewImageFromHtml(html, variant);
        const image = ensurePreviewImage();

        if (imageData?.src && image) {
          image.src = imageData.src;
          image.alt = imageData.alt || '';
          preview.hidden = false;
        } else if (!previewOptions.textContent.trim()) {
          preview.hidden = true;
        }

        previewOptions.textContent = getSelectedOptionsText();
      };

      const syncQuantityDisabled = () => {
        const input = getMainQuantityInput();
        const value = parseInt(quantityInput.value || input?.value || '1');
        const min = parseInt(quantityInput.min || '1');
        const max = quantityInput.max ? parseInt(quantityInput.max) : null;

        quantityMinus.classList.toggle('is-disabled', value <= min);
        quantityMinus.toggleAttribute('disabled', value <= min);
        quantityPlus.classList.toggle('is-disabled', max !== null && value >= max);
        quantityPlus.toggleAttribute('disabled', max !== null && value >= max);
      };

      const syncQuantity = () => {
        const input = getMainQuantityInput();
        if (!input) {
          quantityRow.hidden = true;
          return;
        }

        quantityRow.hidden = false;
        quantityInput.value = input.value || input.min || '1';
        quantityInput.min = input.min || input.dataset.min || '1';
        quantityInput.step = input.step || '1';

        if (input.max || input.dataset.max) {
          quantityInput.max = input.max || input.dataset.max;
        } else {
          quantityInput.removeAttribute('max');
        }

        syncQuantityDisabled();
        syncSubtotalPrice();
      };

      const setMainQuantity = (value) => {
        const input = getMainQuantityInput();
        if (!input) return;

        input.value = String(value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncQuantity();
      };

      const changeQuantity = (direction) => {
        const step = parseInt(quantityInput.step || '1');
        const min = parseInt(quantityInput.min || '1');
        const max = quantityInput.max ? parseInt(quantityInput.max) : null;
        const current = parseInt(quantityInput.value || String(min));
        let next = current + step * direction;

        next = Math.max(next, min);
        if (max !== null) next = Math.min(next, max);

        setMainQuantity(next);
      };

      const handleQuantityMinus = () => changeQuantity(-1);
      const handleQuantityPlus = () => changeQuantity(1);
      const handleQuantityChange = () => {
        const min = parseInt(quantityInput.min || '1');
        const max = quantityInput.max ? parseInt(quantityInput.max) : null;
        let value = parseInt(quantityInput.value || String(min));

        value = Math.max(value, min);
        if (max !== null) value = Math.min(value, max);
        setMainQuantity(value);
      };

      const cancelCloseCleanup = () => {
        if (closeTimeout) {
          window.clearTimeout(closeTimeout);
          closeTimeout = null;
        }

        if (closeTransitionHandler) {
          drawerPanel.removeEventListener('transitionend', closeTransitionHandler);
          closeTransitionHandler = null;
        }
      };

      const finishDrawerClose = () => {
        cancelCloseCleanup();
        if (drawer.classList.contains('is-open')) return;
        document.documentElement.classList.remove('gm-sticky-atc-lock');
        const callback = afterDrawerClose;
        afterDrawerClose = null;
        callback?.();
      };

      const releaseDrawerFocus = (restoreFocus = true) => {
        if (focusFrame) {
          window.cancelAnimationFrame(focusFrame);
          focusFrame = null;
        }

        const focusTarget = restoreFocus && previousFocus && document.contains(previousFocus) ? previousFocus : null;
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(focusTarget);
        } else if (focusTarget) {
          focusTarget.focus({ preventScroll: true });
        }
        previousFocus = null;
      };

      const closeDrawer = (options = {}) => {
        const restoreFocus = options?.restoreFocus !== false;
        const onClosed = typeof options?.onClosed === 'function' ? options.onClosed : null;

        if (!drawer.classList.contains('is-open')) {
          setDrawerIntent('cart');
          onClosed?.();
          return;
        }
        cancelCloseCleanup();
        afterDrawerClose = onClosed;

        closeTransitionHandler = (event) => {
          if (event.target !== drawerPanel || event.propertyName !== 'transform') return;
          finishDrawerClose();
        };
        drawerPanel.addEventListener('transitionend', closeTransitionHandler);

        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        releaseDrawerFocus(restoreFocus);
        setDrawerIntent('cart');
        document.dispatchEvent(new CustomEvent('gm:layer:update'));
        if (window.gmScheduleLayerSync) window.gmScheduleLayerSync();
        syncButton();
        closeTimeout = window.setTimeout(finishDrawerClose, 450);
      };

      const isDrawerOpen = () => drawer.classList.contains('is-open');

      const openDrawer = (intent = 'cart') => {
        cancelCloseCleanup();
        afterDrawerClose = null;
        mountDrawer();
        setDrawerIntent(intent);

        buildOptions();
        syncPreview();
        syncLeadTime();
        syncQuantity();
        previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('gm-sticky-atc-lock');
        document.dispatchEvent(new CustomEvent('gm:layer:update'));
        if (window.gmScheduleLayerSync) window.gmScheduleLayerSync();
        syncButton();

        const focusTarget = drawer.querySelector('.gm-sticky-atc-drawer__close') || drawerPanel;
        focusFrame = window.requestAnimationFrame(() => {
          focusFrame = null;
          if (!drawer.classList.contains('is-open')) return;
          if (typeof trapFocus === 'function') {
            trapFocus(drawerPanel, focusTarget);
          } else {
            focusTarget.focus({ preventScroll: true });
          }
        });
      };

      const handleDrawerKeyup = (event) => {
        if ((event.key === 'Escape' || event.code === 'Escape') && drawer.classList.contains('is-open')) {
          event.preventDefault();
          closeDrawer();
        }
      };

      const handleStickyAction = () => {
        if (isStudioPurchase) {
          const variantPicker = getVariantPicker();
          const scrollTarget = variantPicker || mainButton;
          const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

          window.scrollTo({
            top: Math.max(window.pageYOffset + scrollTarget.getBoundingClientRect().top - getHeaderOffset(), 0),
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
          });
          return;
        }

        if (!isDrawerOpen()) {
          openDrawer();
          return;
        }

        if (drawerIntent === 'gift') {
          const anyGiftButton = getAnyGiftButton();
          if (variantPending || !anyGiftButton || !getSelectedVariant()?.available) {
            syncButton();
            return;
          }

          closeDrawer({
            restoreFocus: false,
            onClosed: () => {
              const currentAnyGiftButton = document.contains(anyGiftButton) ? anyGiftButton : getAnyGiftButton();
              if (!currentAnyGiftButton) return;

              allowNativeAnyGiftClick = true;
              try {
                currentAnyGiftButton.click();
              } finally {
                allowNativeAnyGiftClick = false;
              }
            }
          });
          return;
        }

        closeDrawer();
        mainButton.click();
      };

      const handleLaunch = () => {
        openDrawer();
      };

      const handleAnyGiftClick = (event) => {
        const target = event.target instanceof Element ? event.target.closest(anyGiftButtonSelector) : null;
        if (
          !target ||
          !root.contains(target) ||
          allowNativeAnyGiftClick ||
          window.innerWidth >= 990 ||
          !hasSelectableOptions()
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        openDrawer('gift');
      };

      const handleReviewLink = () => {
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        const getReviewTarget = () =>
          document.querySelector('[id^="shopify-section-"][id$="__17676183616b49e6b2"]') ||
          document.querySelector('#judgeme_product_reviews') ||
          document.querySelector('.jdgm-review-widget') ||
          document.querySelector('[data-widget-name="review_widget"]') ||
          document.querySelector('[id*="17676183616b49e6b2"]');

        const scrollToReviewTarget = (behavior = 'smooth') => {
          const reviewTarget = getReviewTarget();
          if (!reviewTarget) return false;

          window.scrollTo({
            top: Math.max(window.pageYOffset + reviewTarget.getBoundingClientRect().top - getHeaderOffset(), 0),
            behavior: prefersReducedMotion ? 'auto' : behavior
          });
          return true;
        };

        if (!scrollToReviewTarget()) return;

        const calibrationTimers = [
          window.setTimeout(() => scrollToReviewTarget('auto'), 300),
          window.setTimeout(() => scrollToReviewTarget('auto'), 900)
        ];
        const cancelCalibration = () => {
          calibrationTimers.forEach((timer) => window.clearTimeout(timer));
          window.removeEventListener('touchstart', cancelCalibration);
          window.removeEventListener('wheel', cancelCalibration);
          window.removeEventListener('pointerdown', cancelCalibration);
          window.removeEventListener('keydown', cancelCalibration);
        };

        window.addEventListener('touchstart', cancelCalibration, { once: true, passive: true });
        window.addEventListener('wheel', cancelCalibration, { once: true, passive: true });
        window.addEventListener('pointerdown', cancelCalibration, { once: true });
        window.addEventListener('keydown', cancelCalibration, { once: true });
      };

      const dispatchMainChange = (input) => {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      const getCurrentOptionValues = () => {
        const picker = getVariantPicker();
        if (!picker) return [];

        return Array.from(picker.querySelectorAll('.product-form__input')).map((group) => {
          const selectedInput = group.querySelector('input[type="radio"]:checked');
          const selectedSelect = group.querySelector('select');
          return selectedInput?.value || selectedSelect?.value || '';
        });
      };

      const getSelectedVariant = () => {
        if (!productVariants.length) return mainButton.hasAttribute('disabled') ? null : { available: true };

        const selectedValues = getCurrentOptionValues();
        return productVariants.find((variant) =>
          selectedValues.every((value, index) => !value || variant.options[index] === value)
        );
      };

      const markVariantPending = () => {
        variantPending = true;
        if (variantPendingTimeout) window.clearTimeout(variantPendingTimeout);
        variantPendingTimeout = window.setTimeout(() => {
          variantPendingTimeout = null;
          variantPending = false;
          syncButton();
        }, 1200);
        syncButton();
      };

      const markVariantReady = () => {
        if (variantPendingTimeout) {
          window.clearTimeout(variantPendingTimeout);
          variantPendingTimeout = null;
        }
        variantPending = false;
      };

      const findBestVariantForSelection = (changedIndex, changedValue) => {
        const selectedValues = getCurrentOptionValues();
        selectedValues[changedIndex] = changedValue;
        const fullSelectionMatches = (variant) =>
          selectedValues.every((value, index) => !value || variant.options[index] === value);
        const changedPathMatches = (variant) =>
          selectedValues.slice(0, changedIndex + 1).every((value, index) => !value || variant.options[index] === value);

        return (
          productVariants.find((variant) => fullSelectionMatches(variant)) ||
          productVariants.find((variant) => variant.available && changedPathMatches(variant)) ||
          productVariants.find((variant) => changedPathMatches(variant))
        );
      };

      const optionValueExistsForCurrentPath = (optionIndex, optionValue) => {
        if (!productVariants.length) return true;

        const selectedValues = getCurrentOptionValues();
        return productVariants.some(
          (variant) =>
            variant.options[optionIndex] === optionValue &&
            selectedValues.slice(0, optionIndex).every((value, index) => !value || variant.options[index] === value)
        );
      };

      const setMainGroupValue = (group, value, shouldDispatch) => {
        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
        const select = group.querySelector('select');

        if (radio) {
          radio.checked = true;
          if (shouldDispatch) dispatchMainChange(radio);
          return;
        }

        if (select) {
          select.value = value;
          Array.from(select.options).forEach((option) => {
            option.toggleAttribute('selected', option.value === value);
          });
          if (shouldDispatch) dispatchMainChange(select);
        }
      };

      const setMainVariantFromOption = (changedIndex, changedValue) => {
        const picker = getVariantPicker();
        const groups = Array.from(picker?.querySelectorAll('.product-form__input') || []);
        const targetVariant = findBestVariantForSelection(changedIndex, changedValue);

        if (!targetVariant) return;

        markVariantPending();

        groups.forEach((group, index) => {
          const nextValue = targetVariant.options[index];
          if (!nextValue) return;

          setMainGroupValue(group, nextValue, index === groups.length - 1);
        });
      };

      const cleanLabelText = (label) => {
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.visually-hidden, .label-unavailable').forEach((element) => element.remove());
        return clone.textContent.trim();
      };

      const getOptionName = (group, fallback) => {
        const label = group.querySelector('.form__label');
        if (!label) return fallback;

        const clone = label.cloneNode(true);
        clone.querySelectorAll('[data-selected-value], .dropdown-swatch').forEach((element) => element.remove());
        return clone.textContent.replace(/\s+/g, ' ').trim().replace(/\s*:\s*$/, '') || fallback;
      };

      const buildOptions = () => {
        const picker = getVariantPicker();
        optionsContainer.innerHTML = '';

        if (!picker) {
          optionsContainer.hidden = true;
          return;
        }

        optionsContainer.hidden = false;

        Array.from(picker.querySelectorAll('.product-form__input')).forEach((group, groupIndex) => {
          const option = document.createElement('div');
          option.className = 'gm-sticky-atc-drawer__option';

          const title = document.createElement('p');
          title.className = 'gm-sticky-atc-drawer__option-name';
          title.textContent = getOptionName(group, `Option ${groupIndex + 1}`);
          option.appendChild(title);

          const select = group.querySelector('select');

          if (select) {
            const mirrorSelect = document.createElement('select');
            mirrorSelect.className = 'gm-sticky-atc-drawer__select';
            mirrorSelect.setAttribute('aria-label', title.textContent);

            Array.from(select.options).forEach((sourceOption) => {
              if (!optionValueExistsForCurrentPath(groupIndex, sourceOption.value)) return;

              const optionEl = document.createElement('option');
              optionEl.value = sourceOption.value;
              optionEl.textContent = sourceOption.textContent.trim();
              optionEl.dataset.unavailable = String(sourceOption.disabled);
              optionEl.selected = sourceOption.selected;
              mirrorSelect.appendChild(optionEl);
            });

            mirrorSelect.addEventListener('change', () => setMainVariantFromOption(groupIndex, mirrorSelect.value));
            option.appendChild(mirrorSelect);
          } else {
            const values = document.createElement('div');
            values.className = 'gm-sticky-atc-drawer__values';

            Array.from(group.querySelectorAll('input[type="radio"]')).forEach((input) => {
              if (!optionValueExistsForCurrentPath(groupIndex, input.value)) return;

              const label = Array.from(group.querySelectorAll('label')).find((label) => label.htmlFor === input.id);
              const valueButton = document.createElement('button');
              const disabled = input.disabled || input.classList.contains('disabled') || input.classList.contains('visually-disabled');

              valueButton.type = 'button';
              valueButton.className = 'gm-sticky-atc-drawer__value';
              valueButton.textContent = label ? cleanLabelText(label) : input.value;
              valueButton.classList.toggle('is-selected', input.checked);
              valueButton.classList.toggle('is-disabled', disabled);
              valueButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
              valueButton.addEventListener('click', () => setMainVariantFromOption(groupIndex, input.value));
              values.appendChild(valueButton);
            });

            option.appendChild(values);
          }

          optionsContainer.appendChild(option);
        });
      };

      let mainButtonPassed = false;
      const updateVisibility = () => {
        const visible = window.innerWidth < 990 && (!isStudioPurchase || mainButtonPassed);
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      const mainButtonObserverBottomMargin = Math.max(document.documentElement.scrollHeight, window.innerHeight);

      const mainButtonObserver =
        isStudioPurchase && 'IntersectionObserver' in window
          ? new IntersectionObserver(
              ([entry]) => {
                mainButtonPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                updateVisibility();
              },
              { rootMargin: `0px 0px ${mainButtonObserverBottomMargin}px 0px`, threshold: 0 }
            )
          : null;

      mainButtonObserver?.observe(mainButton);

      const onClick = () => {
        window.setTimeout(syncButton, 0);
      };

      const mutationObserver = new MutationObserver(syncButton);
      mutationObserver.observe(mainButton, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });

      const variantUnsubscriber =
        typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
          ? subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              markVariantReady();
              syncPrice(event?.data?.html);
              syncPreview(event?.data?.variant, event?.data?.html);
              buildOptions();
              syncLeadTime();
              syncQuantity();
              window.setTimeout(syncButton, 0);
            })
          : null;

      const changeUnsubscriber =
        typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
          ? subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
              markVariantPending();
              window.setTimeout(() => {
                buildOptions();
                syncPreview();
                syncLeadTime();
                syncQuantity();
                syncButton();
              }, 0);
            })
          : null;

      actionButtons.forEach((button) => button.addEventListener('click', handleStickyAction));
      launchButtons.forEach((button) => button.addEventListener('click', handleLaunch));
      root.addEventListener('click', handleAnyGiftClick, true);
      root.setAttribute('data-sticky-atc-ready', 'true');
      if (anyGiftSkeleton && !resolveAnyGiftSkeleton()) {
        anyGiftSkeleton.hidden = false;
        anyGiftSkeletonObserver = new MutationObserver(resolveAnyGiftSkeleton);
        anyGiftSkeletonObserver.observe(root, { childList: true, subtree: true });
        anyGiftSkeletonTimeout = window.setTimeout(() => {
          anyGiftSkeleton.hidden = true;
          cleanupAnyGiftSkeleton();
        }, 6000);
      }
      closeButtons.forEach((button) => button.addEventListener('click', closeDrawer));
      drawer.addEventListener('keyup', handleDrawerKeyup);
      drawerSubmit.addEventListener('click', closeDrawer);
      quantityMinus.addEventListener('click', handleQuantityMinus);
      quantityPlus.addEventListener('click', handleQuantityPlus);
      quantityInput.addEventListener('change', handleQuantityChange);
      reviewLinks.forEach((button) => button.addEventListener('click', handleReviewLink));
      window.addEventListener('resize', updateVisibility);
      buildOptions();
      syncPreview();
      syncLeadTime();
      syncQuantity();
      syncButton();
      syncPrice();
      updateVisibility();

      stickyAtcControllers.set(sectionKey, {
        actionButtons,
        launchButtons,
        closeButtons,
        drawerSubmit,
        quantityMinus,
        quantityPlus,
        quantityInput,
        reviewLinks,
        root,
        bar,
        drawer,
        originalParent,
        originalNextSibling,
        drawerOriginalParent,
        drawerOriginalNextSibling,
        mutationObserver,
        mainButtonObserver,
        variantUnsubscriber,
        changeUnsubscriber,
        onAction: handleStickyAction,
        onLaunch: handleLaunch,
        onClose: closeDrawer,
        onKeyup: handleDrawerKeyup,
        onQuantityMinus: handleQuantityMinus,
        onQuantityPlus: handleQuantityPlus,
        onQuantityChange: handleQuantityChange,
        onReviewLink: handleReviewLink,
        onAnyGiftClick: handleAnyGiftClick,
        onClick,
        onUpdate: updateVisibility,
        cancelCloseCleanup,
        cleanupAnyGiftSkeleton,
        releaseDrawerFocus,
        clearVariantPending: markVariantReady,
        get closeTimeout() {
          return closeTimeout;
        }
      });

      if (window.gmScheduleLayerSync) window.gmScheduleLayerSync();
    };

    const scheduleFabRetry = (root, retryCount) => {
      const sectionKey = getSectionKey(root);
      clearFabRetry(sectionKey);

      const timeoutId = window.setTimeout(() => {
        fabRetryTimers.delete(sectionKey);
        initScrollFab(root, retryCount + 1);
      }, 300);

      fabRetryTimers.set(sectionKey, timeoutId);
    };

    const initScrollFab = (root, retryCount = 0) => {
      const sectionKey = getSectionKey(root);
      destroyFab(sectionKey);

      const fab = document.getElementById(`gm-scroll-fab-${sectionKey}`);
      const fabButton = fab?.querySelector('[data-scroll-fab-button]');

      if (!fab || !fabButton) return;

      const getElements = () => ({
        trigger:
          document.querySelector('[data-product-details-anchor] .p-product-details__description') ||
          document.querySelector('[data-product-details-anchor]') ||
          document.querySelector('.p-product-details__description') ||
          document.querySelector('.p-product-details'),
        purchaseForm: root.querySelector('[data-product-purchase-form]') || root.querySelector('form[action*="/cart/add"]'),
        scrollTarget:
          root.querySelector('[data-product-purchase-target]') ||
          root.querySelector('[data-product-scroll-target]') ||
          root.querySelector('variant-radios, variant-selects, form[action*="/cart/add"]'),
        purchaseArea:
          root.querySelector('[data-product-purchase-area]') ||
          root.querySelector('.product__info-container') ||
          root.querySelector('.product-form') ||
          root.querySelector('form[action*="/cart/add"]')
      });

      const elements = getElements();

      if (!elements.trigger || !elements.purchaseForm || !elements.scrollTarget || !elements.purchaseArea) {
        if (retryCount < 12) {
          scheduleFabRetry(root, retryCount);
        }
        return;
      }

      const isInViewport = (element) => {
        const rect = element.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      };

      const getHeaderOffset = () => {
        const header =
          document.querySelector('#shopify-section-header') ||
          document.querySelector('.section-header') ||
          document.querySelector('header.header') ||
          document.querySelector('.shopify-section-group-header-group');

        if (!header) return 16;

        return Math.max(header.getBoundingClientRect().height, 16) + 12;
      };

      const setFabVisible = (visible) => {
        fab.classList.toggle('is-visible', visible);
        fab.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };

      const updateFabVisibility = () => {
        const current = getElements();
        if (window.innerWidth >= 990 || root.querySelector('[data-sticky-atc]') || !current.trigger || !current.purchaseArea) {
          setFabVisible(false);
          return;
        }

        const reachedDetails = current.trigger.getBoundingClientRect().top <= window.innerHeight * 0.85;
        const purchaseVisible = isInViewport(current.purchaseArea);
        const footer = document.querySelector('footer');
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;

        setFabVisible(reachedDetails && !purchaseVisible && !footerVisible);
      };

      const scrollToPurchaseArea = () => {
        const current = getElements();
        if (!current.scrollTarget) return;

        const targetRect = current.scrollTarget.getBoundingClientRect();
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({
          top: Math.max(window.pageYOffset + targetRect.top - getHeaderOffset(), 0),
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      };

      const deferredUpdate = () => {
        window.setTimeout(updateFabVisibility, 50);
      };

      fabButton.addEventListener('click', scrollToPurchaseArea);
      setFabVisible(false);
      updateFabVisibility();

      window.addEventListener('scroll', updateFabVisibility, { passive: true });
      window.addEventListener('resize', updateFabVisibility);
      document.addEventListener('variant:change', deferredUpdate);
      document.addEventListener('change', deferredUpdate);

      fabControllers.set(sectionKey, {
        button: fabButton,
        onClick: scrollToPurchaseArea,
        onUpdate: updateFabVisibility,
        onDeferredUpdate: deferredUpdate
      });
    };

    const init = (scope = document) => {
      queryRoots(scope).forEach((root) => {
        initLeadTime(root);
        initStickyAtc(root);
      });
    };

    const destroy = (scope = document) => {
      queryRoots(scope).forEach((root) => {
        const sectionKey = getSectionKey(root);
        destroyLeadTime(sectionKey);
        destroyStickyAtc(sectionKey);
        destroyFab(sectionKey);
      });
    };

    if (document.readyState !== 'loading') {
      init();
    } else {
      document.addEventListener('DOMContentLoaded', () => init(), { once: true });
    }

    document.addEventListener('product-info:loaded', (event) => init(event.target));
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  })();
}
